import { defineSlotRecipe } from "@pandacss/dev";

export const slots = [
  "wrap",
  "description",
  "menuTrigger",
  "datesWrap",
  "title",
];

export const aromaMemoBadge = defineSlotRecipe({
  className: "aroma-memo-badge",
  jsx: ["MemoBadge"],
  slots,
  base: {
    wrap: {
      position: "relative",
      width: "100%",
      paddingX: "padding.3",
      flexDirection: "column",
      gap: "gap.2",
      borderStyle: "solid",
      borderWidth: "m",
      overflow: "hidden",
      _group: true,
      _hover: {
        cursor: "pointer",
      },
      _focusVisible: {
        shadow: "effect.focus-ring.brand",
        outline: "none",
      },
      _invalid: {
        borderColor: "border.neutral.subtlest",
        shadow: "effect.focus-ring.error",
      },
    },
    title: {
      display: "inline-block",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    menuTrigger: {
      position: "relative",
      top: "padding.1",
      left: "padding.1",
      opacity: 0,
    },
    datesWrap: {
      width: "100%",
      minHeight: "m",
      display: "flex",
      gap: "gap.2",
      justifyContent: "space-between",
      alignItems: "flex-end",
    },
  },
  variants: {
    size: {
      small: {
        wrap: {
          paddingX: "padding.1",
          paddingY: "calc({spacing.padding.0,5} + {borderWidths.m})",
          borderRadius: "s",
          height: "min-content",
        },
        description: {
          display: "none",
        },
        datesWrap: {
          display: "none",
        },
      },
      medium: {
        wrap: {
          paddingY: "padding.2",
          borderRadius: "s",
          height: "m",
        },
        title: {
          whiteSpace: "nowrap",
        },
        description: {
          display: "none",
        },
        datesWrap: {
          display: "none",
        },
      },
      large: {
        wrap: {
          paddingY: "padding.3",
          borderRadius: "l",
          height: "auto",
        },
        title: {
          whiteSpace: "wrap",
        },
        menuTrigger: {
          _groupHover: {
            opacity: 1,
          },
        },
      },
    },
    colorTheme: {
      pink: {
        wrap: {
          backgroundColor: "background.decorative.pink.base-subtle",
          borderColor: "border.decorative.pink",
          color: "content.decorative.pink",
          _hover: {
            backgroundColor: "background.decorative.pink.hover-subtle",
          },
        },
      },
      purple: {
        wrap: {
          backgroundColor: "background.decorative.purple.base-subtle",
          borderColor: "border.decorative.purple",
          color: "content.decorative.purple",
          _hover: {
            backgroundColor: "background.decorative.purple.hover-subtle",
          },
        },
      },
      blue: {
        wrap: {
          backgroundColor: "background.decorative.blue.base-subtle",
          borderColor: "border.decorative.blue",
          color: "content.decorative.blue",
          _hover: {
            backgroundColor: "background.decorative.blue.hover-subtle",
          },
        },
      },
      green: {
        wrap: {
          backgroundColor: "background.decorative.green.base-subtle",
          borderColor: "border.decorative.green",
          color: "content.decorative.green",
          _hover: {
            backgroundColor: "background.decorative.green.hover-subtle",
          },
        },
      },
      yellow: {
        wrap: {
          backgroundColor: "background.decorative.yellow.base-subtle",
          borderColor: "border.decorative.yellow",
          color: "content.decorative.yellow",
          _hover: {
            backgroundColor: "background.decorative.yellow.hover-subtle",
          },
        },
      },
      orange: {
        wrap: {
          backgroundColor: "background.decorative.orange.base-subtle",
          borderColor: "border.decorative.orange",
          color: "content.decorative.orange",
          _hover: {
            backgroundColor: "background.decorative.orange.hover-subtle",
          },
        },
      },
      red: {
        wrap: {
          backgroundColor: "background.decorative.red.base-subtle",
          borderColor: "border.decorative.red",
          color: "content.decorative.red",
          _hover: {
            backgroundColor: "background.decorative.red.hover-subtle",
          },
        },
      },
      neutral: {
        wrap: {
          backgroundColor: "background.decorative.neutral.base-subtle",
          borderColor: "border.decorative.neutral",
          color: "content.neutral.base.bold",
          _hover: {
            backgroundColor: "background.decorative.neutral.hover-subtle",
          },
        },
      },
    },
    warning: {
      true: {
        wrap: {
          shadow: "effect.focus-ring.warning",
        },
      },
      false: {},
    },
    disabled: {
      true: {
        wrap: {
          backgroundColor: "background.neutral.disable.subtle",
          borderColor: "background.neutral.disable.subtle",
          color: "content.neutral.base.disable",
          _hover: {
            backgroundColor: "background.neutral.disable.subtle",
            borderColor: "background.neutral.disable.subtle",
            color: "content.neutral.base.disable",
          },
        },
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      size: "small",
      colorTheme: "pink",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.pink.base-bolder",
            borderColor: "background.decorative.pink.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "small",
      colorTheme: "purple",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.purple.base-bolder",
            borderColor: "background.decorative.purple.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "small",
      colorTheme: "blue",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.blue.base-bolder",
            borderColor: "background.decorative.blue.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "small",
      colorTheme: "green",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.green.base-bolder",
            borderColor: "background.decorative.green.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "small",
      colorTheme: "yellow",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.yellow.base-bolder",
            borderColor: "background.decorative.yellow.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "small",
      colorTheme: "orange",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.orange.base-bolder",
            borderColor: "background.decorative.orange.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "small",
      colorTheme: "red",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.red.base-bolder",
            borderColor: "background.decorative.red.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "small",
      colorTheme: "neutral",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.neutral.base-bolder",
            borderColor: "background.decorative.neutral.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "pink",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.pink.base-bolder",
            borderColor: "background.decorative.pink.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "purple",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.purple.base-bolder",
            borderColor: "background.decorative.purple.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "blue",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.blue.base-bolder",
            borderColor: "background.decorative.blue.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "green",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.green.base-bolder",
            borderColor: "background.decorative.green.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "yellow",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.yellow.base-bolder",
            borderColor: "background.decorative.yellow.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "orange",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.orange.base-bolder",
            borderColor: "background.decorative.orange.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "red",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.red.base-bolder",
            borderColor: "background.decorative.red.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
    {
      size: "medium",
      colorTheme: "neutral",
      css: {
        wrap: {
          _active: {
            backgroundColor: "background.decorative.neutral.base-bolder",
            borderColor: "background.decorative.neutral.base-bolder",
            color: "content.neutral.reverse.bold",
          },
        },
      },
    },
  ],
  defaultVariants: {
    size: "small",
    colorTheme: "neutral",
  },
});
